import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { MarkdownView } from '../components/MarkdownView';
import { TEMPLATES, resolveTemplate, LOGO_SIZES } from '../components/welcomeTemplates';
import { useCookieConsent } from '../hooks/useCookieConsent';
import { CookieConsentBanner } from '../components/CookieConsentBanner';

/**
 * WelcomePage — Public, no-auth, per-organisation branded marketing
 * page reachable at /welcome/:slug. Content is written in Markdown by
 * the org admin. Look-and-feel comes from a pluggable "template" so
 * each nursery can pick a vibe (classic / playful / bold / warm /
 * minimal) while keeping the same underlying content model.
 *
 * All layout choices below flow from the `t` (resolved template)
 * object — this keeps the JSX shared across templates and prevents
 * one branch from drifting behind the others.
 */
export default function WelcomePage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.getPublicWelcomePage(slug);
        if (cancelled) return;
        setData(res);
        const wp = res.welcome_page || {};
        document.title = `${wp.headline || res.org_name || 'Welcome'} · ${res.org_name || 'Kids WiseApp'}`;
        const meta = document.querySelector('meta[name="description"]') ||
                     (() => { const m = document.createElement('meta'); m.name = 'description'; document.head.appendChild(m); return m; })();
        meta.content = (wp.tagline || `Welcome to ${res.org_name}`).slice(0, 160);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Page not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // ---- Cookie consent ----
  // Loaded per-slug so visiting a second nursery re-prompts the visitor.
  // Analytics injection is strictly gated on `analyticsAllowed` — see
  // effect below.
  const consent = useCookieConsent(slug);

  // Detect whether the org has ANY analytics configured. If not there's
  // no legal basis to prompt for consent at all — banner stays hidden
  // and the page renders unencumbered.
  const hasAnyAnalytics = useMemo(() => {
    const wp = data?.welcome_page || {};
    return !!(
      wp.ga4_measurement_id ||
      wp.facebook_pixel_id ||
      wp.plausible_domain ||
      (wp.custom_head_snippet && wp.custom_head_snippet.trim())
    );
  }, [data]);

  // Inject 3rd-party analytics tags AFTER the visitor grants consent.
  // Without consent (or when the visitor has actively rejected), we
  // never insert any tracking script — this is what makes the page
  // GDPR/PECR-compliant end-to-end.
  useEffect(() => {
    if (!data) return undefined;
    if (!consent.analyticsAllowed) return undefined;
    const wp = data.welcome_page || {};
    const injected = injectAnalytics(wp);
    return () => { injected.forEach((node) => node.remove()); };
  }, [data, consent.analyticsAllowed]);

  // Fired by SignupCard after a successful submission. Notifies every
  // configured analytics provider with a shared "signup completed"
  // conversion event so nurseries can measure their marketing.
  const trackSignupCompleted = () => {
    if (!consent.analyticsAllowed) return; // no consent → no tracking
    try {
      if (typeof window === 'undefined') return;
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'sign_up', {
          method: 'welcome_page',
          event_category: 'welcome_page',
          event_label: data?.slug,
        });
      }
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', { source: 'welcome_page' });
      }
      if (typeof window.plausible === 'function') {
        window.plausible('Signup', { props: { source: 'welcome_page' } });
      }
    } catch (_e) { /* never let analytics break the app */ }
  };

  if (loading) {
    return (
      <div style={centered} data-testid="welcome-page-loading">
        <div style={{ color: 'hsl(var(--muted-foreground))' }}>Loading…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ ...centered, padding: '2rem' }} data-testid="welcome-page-error">
        <div style={{ textAlign: 'center', maxWidth: 420 }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🌱</div>
          <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.35rem' }}>Page not found</h1>
          <p style={{ color: 'hsl(var(--muted-foreground))', marginBottom: '1rem' }}>
            {error || 'This nursery hasn\'t published their welcome page yet.'}
          </p>
          <Link to={slug ? `/login/${slug}` : '/login'} className="btn btn-primary" data-testid="welcome-notfound-signin">Go to sign in</Link>
        </div>
      </div>
    );
  }

  const wp = data.welcome_page || {};
  const t = resolveTemplate(wp.template, data.primary_color);
  const logoSize = LOGO_SIZES[wp.logo_size] || LOGO_SIZES.m;

  return (
    <div
      data-testid="welcome-page"
      data-template={t.id}
      style={{
        minHeight: '100vh',
        background: t.pageBg,
        color: t.text,
        fontFamily: t.bodyFont,
      }}
    >
      {/* Grain / noise overlay for the "premium polish" texture. Kept at
          low opacity per design guidelines (2-5%). */}
      <GrainOverlay opacity={t.grainOpacity} />

      <TopBar
        orgName={data.org_name}
        logoUrl={data.logo_url}
        primary={t.primary}
        template={t}
        logoSize={logoSize}
        slug={slug}
      />

      <Hero
        orgName={data.org_name}
        logoUrl={data.logo_url}
        wp={wp}
        template={t}
        logoSize={logoSize}
      />

      <main style={{
        maxWidth: 1180, margin: '0 auto',
        padding: '2rem 1.25rem 4rem',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.4fr) minmax(320px, 0.95fr)',
        gap: '2.75rem',
        alignItems: 'start',
        position: 'relative', zIndex: 1,
      }} className="welcome-grid">
        <section data-testid="welcome-content" style={{ display: 'grid', gap: '1.25rem' }}>
          {wp.about_md && (
            <TemplateCard t={t} testId="welcome-about" title="About us" delay={0}>
              <MarkdownView source={wp.about_md} testId="welcome-about-md" />
            </TemplateCard>
          )}

          {t.dividers && wp.about_md && wp.services_md && <SectionDivider t={t} />}

          {wp.services_md && (
            <TemplateCard t={t} testId="welcome-services" title="What we offer" delay={80}>
              <MarkdownView source={wp.services_md} testId="welcome-services-md" />
            </TemplateCard>
          )}

          {t.dividers && wp.services_md && wp.show_opening_hours && wp.opening_hours_md && <SectionDivider t={t} />}

          {wp.show_opening_hours && wp.opening_hours_md && (
            <TemplateCard t={t} testId="welcome-hours" title="Opening hours" delay={140}>
              <MarkdownView source={wp.opening_hours_md} testId="welcome-hours-md" />
            </TemplateCard>
          )}

          {t.dividers && wp.show_opening_hours && wp.show_contact && <SectionDivider t={t} />}

          {wp.show_contact && (
            <TemplateCard t={t} testId="welcome-contact" title="Visit or contact us" delay={200}>
              <ContactBlock data={data} t={t} />
            </TemplateCard>
          )}
        </section>

        <aside style={{ position: 'sticky', top: '1.25rem' }} className="welcome-signup-col">
          <SignupCard
            slug={slug}
            orgName={data.org_name}
            ctaText={wp.cta_text || 'Register your interest'}
            template={t}
            onSignupCompleted={trackSignupCompleted}
          />
        </aside>
      </main>

      <footer style={{
        borderTop: `1px solid ${t.border}`,
        padding: '1.5rem 1.25rem', textAlign: 'center',
        color: t.mutedText, fontSize: '0.8rem',
        position: 'relative', zIndex: 1,
      }}>
        Powered by Kids WiseApp · <Link to={slug ? `/login/${slug}` : '/login'} data-testid="welcome-footer-signin" style={{ color: t.primary }}>Already a parent? Sign in</Link>
        {wp.privacy_policy_url && (
          <>
            {' · '}
            <a
              href={wp.privacy_policy_url}
              target="_blank"
              rel="noreferrer"
              data-testid="welcome-privacy-link"
              style={{ color: t.primary }}
            >Privacy notice</a>
          </>
        )}
        {hasAnyAnalytics && (
          <>
            {' · '}
            <button
              type="button"
              onClick={consent.revoke}
              data-testid="welcome-cookie-preferences"
              style={{
                background: 'transparent', border: 'none',
                color: t.primary, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 'inherit',
                textDecoration: 'underline', padding: 0,
              }}
            >Cookie preferences</button>
          </>
        )}
      </footer>

      {/* Consent banner — only shown when the org has SOMETHING to
          track AND the visitor hasn't already made a choice for this
          slug. Analytics injection above is strictly gated on the
          hook's `analyticsAllowed` boolean. */}
      <CookieConsentBanner
        open={hasAnyAnalytics && !consent.hasChoice}
        privacyPolicyUrl={wp.privacy_policy_url}
        onAcceptAll={consent.acceptAll}
        onRejectAll={consent.rejectAll}
        onSetCategory={consent.setCategory}
        currentCategories={consent.categories}
        primary={t.primary}
      />

      <style>{`
        @keyframes welcomeFadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes welcomeGentle {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-3px); }
        }
        [data-welcome-fade] {
          animation: welcomeFadeUp 0.55s ease both;
        }
        [data-welcome-cta]:hover {
          filter: brightness(1.05);
          transform: translateY(-1px);
        }
        [data-welcome-cta] {
          transition: transform 180ms ease, filter 180ms ease, box-shadow 180ms ease;
        }
        @media (max-width: 900px) {
          .welcome-grid { grid-template-columns: 1fr !important; }
          .welcome-signup-col { position: static !important; }
          .welcome-hero-split { grid-template-columns: 1fr !important; text-align: center !important; }
          .welcome-hero-split img.hero-photo { max-height: 260px !important; }
        }
      `}</style>
    </div>
  );
}

// ------------------------------------------------------------------
// Sub-components
// ------------------------------------------------------------------

function GrainOverlay({ opacity }) {
  if (!opacity) return null;
  // Inline SVG turbulence — no external asset, no request.
  const svg = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        opacity, backgroundImage: svg, mixBlendMode: 'multiply',
      }}
    />
  );
}

function TopBar({ orgName, logoUrl, primary, template: t, logoSize, slug }) {
  return (
    <header style={{
      padding: '0.85rem 1.25rem',
      background: t.topBarBg,
      backdropFilter: 'blur(14px)',
      borderBottom: `1px solid ${t.border}`,
      position: 'sticky', top: 0, zIndex: 10,
    }}>
      <div style={{
        maxWidth: 1180, margin: '0 auto',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', minWidth: 0 }}>
          <LogoBadge
            orgName={orgName}
            logoUrl={logoUrl}
            primary={primary}
            template={t}
            maxWidth={logoSize.topBar}
            maxHeight={logoSize.topBar * 0.6}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontFamily: t.headlineFont,
              fontWeight: 700, fontSize: '1.05rem', color: t.text,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              lineHeight: 1.15,
            }}>{orgName || 'Nursery'}</div>
            <div style={{ fontSize: '0.72rem', color: t.mutedText, marginTop: 1 }}>
              Nursery & childcare
            </div>
          </div>
        </div>
        <Link
          to={slug ? `/login/${slug}` : '/login'}
          className="btn btn-ghost btn-sm"
          data-testid="welcome-topbar-signin"
          style={{
            borderColor: t.primary, color: t.primary,
            fontFamily: t.bodyFont, fontWeight: 600,
          }}
        >Sign in</Link>
      </div>
    </header>
  );
}

function LogoBadge({ orgName, logoUrl, primary, template: t, maxWidth, maxHeight }) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={orgName ? `${orgName} logo` : 'Logo'}
        data-testid="welcome-logo"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
        style={{
          maxWidth, maxHeight,
          height: 'auto', width: 'auto',
          objectFit: 'contain', display: 'block',
        }}
      />
    );
  }
  // Fallback monogram — sized to match the logo box.
  const size = Math.min(maxWidth, maxHeight || 64);
  return (
    <div
      aria-hidden="true"
      style={{
        width: size, height: size,
        borderRadius: t.radius / 1.6,
        background: primary, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 800, fontSize: size * 0.42,
        fontFamily: t.headlineFont,
        boxShadow: t.shadow,
      }}
    >{(orgName || 'K').charAt(0).toUpperCase()}</div>
  );
}

function Hero({ orgName, logoUrl, wp, template: t, logoSize }) {
  const useSplit = t.heroLayout === 'split' && wp.hero_image_url;
  const useFullBleed = t.heroLayout === 'fullbleed' && wp.hero_image_url;
  const headline = wp.headline || `Welcome to ${orgName || 'our nursery'}`;
  const tagline = wp.tagline || '';

  // Big centred hero logo — always shown at heroLogo size regardless of
  // top-bar. This is the "logo is too small" fix from user feedback.
  const heroLogo = (
    <div style={{
      display: 'flex', justifyContent: 'center',
      marginBottom: '1.5rem',
    }}>
      <LogoBadge
        orgName={orgName}
        logoUrl={logoUrl}
        primary={t.primary}
        template={t}
        maxWidth={logoSize.hero}
        maxHeight={logoSize.hero * 0.7}
      />
    </div>
  );

  const headlineNode = (
    <h1
      data-testid="welcome-headline"
      data-welcome-fade
      style={{
        margin: 0,
        fontFamily: t.headlineFont,
        fontSize: 'clamp(2rem, 5vw, 3.4rem)',
        lineHeight: 1.1, fontWeight: t.headlineWeight,
        letterSpacing: t.headlineTracking,
        color: t.headlineColor || t.text,
      }}
    >{headline}</h1>
  );

  const taglineNode = tagline ? (
    <p
      data-testid="welcome-tagline"
      data-welcome-fade
      style={{
        marginTop: '0.85rem',
        fontSize: 'clamp(1rem, 1.6vw, 1.2rem)',
        lineHeight: 1.55,
        color: t.mutedText,
        fontFamily: t.bodyFont,
        maxWidth: 640,
        animationDelay: '80ms',
      }}
    >{tagline}</p>
  ) : null;

  const accentDivider = t.showAccentDivider ? (
    <div style={{
      width: 72, height: 5,
      borderRadius: 3, background: t.primary,
      margin: '1.25rem 0 0.5rem',
    }} />
  ) : null;

  // FULL-BLEED (Bold Modern): edge-to-edge image with overlaid content
  if (useFullBleed) {
    return (
      <section
        data-testid="welcome-hero"
        style={{
          position: 'relative',
          minHeight: '58vh',
          overflow: 'hidden',
          borderBottom: `1px solid ${t.border}`,
        }}
      >
        <img
          src={wp.hero_image_url}
          alt=""
          className="hero-photo"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', display: 'block',
          }}
        />
        <div style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.75) 100%)`,
        }} />
        <div style={{
          position: 'relative', zIndex: 1,
          maxWidth: 1180, margin: '0 auto',
          padding: '4rem 1.25rem 3.5rem',
          minHeight: '58vh',
          display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
          color: '#fff',
        }}>
          {heroLogo}
          <h1
            data-testid="welcome-headline"
            data-welcome-fade
            style={{
              margin: 0,
              fontFamily: t.headlineFont,
              fontSize: 'clamp(2.2rem, 5.5vw, 4rem)',
              lineHeight: 1.05, fontWeight: t.headlineWeight,
              letterSpacing: t.headlineTracking,
              color: '#fff',
              textShadow: '0 4px 24px rgba(0,0,0,0.35)',
              maxWidth: 900,
            }}
          >{headline}</h1>
          {tagline && (
            <p
              data-testid="welcome-tagline"
              data-welcome-fade
              style={{
                marginTop: '1rem',
                fontSize: 'clamp(1.05rem, 1.8vw, 1.35rem)',
                lineHeight: 1.5,
                color: 'rgba(255,255,255,0.9)',
                fontFamily: t.bodyFont,
                maxWidth: 720,
                animationDelay: '80ms',
              }}
            >{tagline}</p>
          )}
        </div>
      </section>
    );
  }

  // SPLIT (Playful, Warm): left copy + right image
  if (useSplit) {
    return (
      <section
        data-testid="welcome-hero"
        style={{ background: t.heroBg, borderBottom: `1px solid ${t.border}` }}
      >
        <div
          className="welcome-hero-split"
          style={{
            maxWidth: 1180, margin: '0 auto',
            padding: '3rem 1.25rem 3.5rem',
            display: 'grid', gridTemplateColumns: '1.05fr 0.95fr',
            gap: '2.5rem', alignItems: 'center',
          }}
        >
          <div>
            {heroLogo}
            {headlineNode}
            {accentDivider}
            {taglineNode}
          </div>
          <div style={{ position: 'relative' }}>
            {/* Decorative blob behind the image (playful template only) */}
            {t.showHeroBlob && (
              <div aria-hidden="true" style={{
                position: 'absolute', inset: '-20px -30px 20px -10px',
                background: t.blobColor,
                borderRadius: '48% 52% 63% 37% / 40% 44% 56% 60%',
                zIndex: 0,
              }} />
            )}
            <img
              src={wp.hero_image_url}
              alt=""
              className="hero-photo"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
              style={{
                position: 'relative', zIndex: 1,
                width: '100%', maxHeight: 420,
                objectFit: 'cover',
                borderRadius: t.radius,
                boxShadow: t.shadow,
                display: 'block',
              }}
            />
          </div>
        </div>
      </section>
    );
  }

  // BAND (Classic, Minimal): centred content over gradient band
  return (
    <section
      data-testid="welcome-hero"
      style={{
        background: t.heroBg,
        borderBottom: `1px solid ${t.border}`,
        position: 'relative',
      }}
    >
      <div style={{
        maxWidth: 900, margin: '0 auto',
        padding: '3.25rem 1.25rem 3.5rem',
        textAlign: 'center',
      }}>
        {heroLogo}
        {headlineNode}
        {accentDivider && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>{accentDivider}</div>
        )}
        {tagline && (
          <p
            data-testid="welcome-tagline"
            data-welcome-fade
            style={{
              margin: '0.85rem auto 0',
              fontSize: 'clamp(1rem, 1.6vw, 1.2rem)',
              lineHeight: 1.55,
              color: t.mutedText,
              fontFamily: t.bodyFont,
              maxWidth: 640,
              animationDelay: '80ms',
            }}
          >{tagline}</p>
        )}
        {wp.hero_image_url && (
          <img
            src={wp.hero_image_url}
            alt=""
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            style={{
              marginTop: '2rem',
              width: '100%', maxHeight: 380, objectFit: 'cover',
              borderRadius: t.radius,
              boxShadow: t.shadow,
            }}
          />
        )}
      </div>
    </section>
  );
}

function TemplateCard({ t, title, testId, delay, children }) {
  return (
    <section
      data-testid={testId}
      data-welcome-fade
      style={{
        background: t.cardBg,
        border: t.cardBorder,
        borderRadius: t.radius,
        boxShadow: t.shadow,
        padding: t.cardPadding,
        animationDelay: `${delay}ms`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {t.cardAccentBar && (
        <div aria-hidden="true" style={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: 5,
          background: t.primary,
        }} />
      )}
      <h2 style={{
        margin: '0 0 0.85rem',
        fontFamily: t.headlineFont,
        fontSize: '1.35rem',
        fontWeight: t.h2Weight,
        color: t.headlineColor || t.text,
        display: 'flex', alignItems: 'center', gap: '0.6rem',
      }}>
        {t.showSectionDot && (
          <span style={{
            display: 'inline-block', width: 8, height: 8,
            borderRadius: '50%', background: t.primary,
          }} />
        )}
        {title}
      </h2>
      <div style={{ color: t.text, lineHeight: 1.7 }}>{children}</div>
    </section>
  );
}

function SectionDivider({ t }) {
  if (t.dividers === 'wavy') {
    return (
      <div aria-hidden="true" style={{ margin: '0.25rem 0', textAlign: 'center' }}>
        <svg width="120" height="16" viewBox="0 0 120 16" style={{ opacity: 0.7 }}>
          <path
            d="M0 8 Q 15 0, 30 8 T 60 8 T 90 8 T 120 8"
            fill="none"
            stroke={t.primary}
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>
      </div>
    );
  }
  if (t.dividers === 'dots') {
    return (
      <div aria-hidden="true" style={{
        textAlign: 'center',
        color: t.primary, opacity: 0.55, letterSpacing: '0.6rem',
        fontSize: '1.2rem',
      }}>• • •</div>
    );
  }
  if (t.dividers === 'thin') {
    return (
      <div aria-hidden="true" style={{
        height: 1, background: t.border,
        margin: '0.25rem 20% 0.25rem 20%',
      }} />
    );
  }
  return null;
}

function ContactBlock({ data, t }) {
  const rows = [];
  if (data.contact_email) rows.push({ icon: '📧', label: 'Email', value: data.contact_email, href: `mailto:${data.contact_email}` });
  if (data.contact_phone) rows.push({ icon: '📞', label: 'Phone', value: data.contact_phone, href: `tel:${data.contact_phone}` });
  if (data.website) rows.push({ icon: '🌐', label: 'Website', value: data.website, href: data.website.startsWith('http') ? data.website : `https://${data.website}` });
  const addr = data.address || {};
  const addrStr = [addr.line1, addr.city, addr.postcode].filter(Boolean).join(', ');
  if (addrStr) rows.push({ icon: '📍', label: 'Address', value: addrStr, href: null });

  if (rows.length === 0) {
    return <p style={{ margin: 0, color: t.mutedText }}>Contact information coming soon.</p>;
  }

  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.65rem' }}>
      {rows.map((r, i) => (
        <li key={i} style={{ fontSize: '0.95rem', display: 'flex', gap: '0.6rem', alignItems: 'baseline' }}>
          <span aria-hidden="true" style={{ fontSize: '1rem' }}>{r.icon}</span>
          <span style={{ color: t.mutedText, minWidth: 70 }}>{r.label}</span>
          {r.href ? (
            <a href={r.href} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>{r.value}</a>
          ) : (
            <span>{r.value}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function SignupCard({ slug, orgName, ctaText, template: t, onSignupCompleted }) {
  const [form, setForm] = useState({
    parent_name: '', email: '', phone: '', child_age: '', message: '', password: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.parent_name.trim() || !form.parent_name.includes(' ')) {
      setError('Please enter your full name.'); return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      setError('Please enter a valid email.'); return;
    }
    const digits = form.phone.replace(/\D+/g, '');
    if (digits.length < 7) {
      setError('Please enter a valid phone number.'); return;
    }
    if (form.password && form.password.length < 8) {
      setError('Password must be at least 8 characters.'); return;
    }
    setBusy(true);
    try {
      const res = await api.submitWelcomePageSignup(slug, {
        parent_name: form.parent_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        child_age: form.child_age.trim() || undefined,
        message: form.message.trim() || undefined,
        password: form.password || undefined,
      });
      setSuccess(res.message || 'Thanks! We\'ll be in touch shortly.');
      toast.success('Signup received');
      // Fire analytics conversion event(s) — no-op if the org hasn't
      // configured any provider. Wrapped in try/catch inside the callback
      // so a broken analytics tag can never break the signup flow.
      if (typeof onSignupCompleted === 'function') onSignupCompleted();
    } catch (err) {
      const msg = err?.message || 'Signup failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const cardStyle = {
    background: t.cardBg,
    border: t.cardBorder,
    borderRadius: t.radius,
    padding: '1.6rem',
    boxShadow: t.signupShadow || t.shadow,
    fontFamily: t.bodyFont,
    color: t.text,
  };

  if (success) {
    return (
      <div data-testid="welcome-signup-success" style={cardStyle}>
        <div style={{ fontSize: '2.4rem', marginBottom: '0.5rem' }}>🎉</div>
        <h2 style={{ margin: '0 0 0.5rem', fontFamily: t.headlineFont, fontSize: '1.3rem', fontWeight: t.h2Weight }}>
          Thanks — you're on the list!
        </h2>
        <p style={{ margin: 0, color: t.mutedText, lineHeight: 1.65 }}>{success}</p>
        <p style={{ marginTop: '0.75rem', fontSize: '0.82rem', color: t.mutedText }}>
          Once your account is reviewed you'll get an email invite to log in and start booking.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="welcome-signup-form" style={cardStyle}>
      <div style={{
        display: 'inline-block',
        padding: '3px 12px', borderRadius: 999,
        background: `${t.primary}22`, color: t.primary,
        fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.5,
        textTransform: 'uppercase', marginBottom: '0.65rem',
        fontFamily: t.bodyFont,
      }}>{ctaText}</div>
      <h2 style={{
        margin: '0 0 0.35rem',
        fontFamily: t.headlineFont, fontSize: '1.45rem',
        fontWeight: t.h2Weight, color: t.headlineColor || t.text,
      }}>
        Join {orgName}
      </h2>
      <p style={{ margin: '0 0 1.1rem', fontSize: '0.9rem', color: t.mutedText }}>
        Fill in your details — we'll review and get in touch.
      </p>

      <Field t={t} label="Full name*" testId="welcome-signup-name">
        <input required value={form.parent_name} onChange={set('parent_name')}
               data-testid="welcome-signup-name-input" placeholder="Jane Smith" />
      </Field>
      <Field t={t} label="Email*" testId="welcome-signup-email">
        <input required type="email" value={form.email} onChange={set('email')}
               data-testid="welcome-signup-email-input" placeholder="you@example.com" />
      </Field>
      <Field t={t} label="Phone*" testId="welcome-signup-phone">
        <input required type="tel" value={form.phone} onChange={set('phone')}
               data-testid="welcome-signup-phone-input" placeholder="+44 7…" />
      </Field>
      <Field t={t} label="Child's age (optional)" testId="welcome-signup-child-age">
        <input value={form.child_age} onChange={set('child_age')}
               data-testid="welcome-signup-child-age-input"
               placeholder="e.g. 2 years 4 months" />
      </Field>
      <Field t={t} label="Anything we should know? (optional)" testId="welcome-signup-msg">
        <textarea rows={3} value={form.message} onChange={set('message')}
                  data-testid="welcome-signup-msg-input"
                  placeholder="Preferred start date, sessions, allergies…" />
      </Field>
      <Field t={t} label="Choose a password (optional)" testId="welcome-signup-password"
             help="Skip and we'll email you a login link once approved.">
        <input type="password" value={form.password} onChange={set('password')}
               data-testid="welcome-signup-password-input"
               placeholder="Min 8 characters" autoComplete="new-password" />
      </Field>

      {error && (
        <div style={{
          borderLeft: '4px solid hsl(0 65% 50%)', color: 'hsl(0 60% 35%)',
          background: 'hsl(0 65% 96%)',
          fontSize: '0.85rem', margin: '0.5rem 0', padding: '0.5rem 0.75rem',
          borderRadius: 6,
        }} data-testid="welcome-signup-error">{error}</div>
      )}

      <button
        type="submit"
        disabled={busy}
        data-testid="welcome-signup-submit"
        data-welcome-cta
        style={{
          width: '100%',
          padding: '0.95rem 1rem', marginTop: '0.5rem',
          borderRadius: t.ctaRadius, border: 'none',
          background: t.ctaBg, color: t.ctaFg,
          fontWeight: 800, fontSize: '0.95rem',
          fontFamily: t.bodyFont,
          letterSpacing: 0.2,
          cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1,
          boxShadow: t.ctaShadow,
        }}
      >{busy ? 'Sending…' : ctaText}</button>

      <p style={{ marginTop: '0.85rem', fontSize: '0.72rem', color: t.mutedText, lineHeight: 1.55 }}>
        By submitting you agree we can contact you about a place at {orgName}. Your details are only shared with the nursery.
      </p>
    </form>
  );
}

function Field({ t, label, help, testId, children }) {
  return (
    <label data-testid={testId} style={{
      display: 'block', margin: '0.7rem 0',
      fontSize: '0.82rem', fontWeight: 600, color: t.text,
      fontFamily: t.bodyFont,
    }}>
      <div style={{ marginBottom: 5 }}>{label}</div>
      {React.cloneElement(children, {
        style: {
          width: '100%', padding: '0.6rem 0.75rem',
          border: `1px solid ${t.border}`, borderRadius: t.inputRadius,
          fontSize: '0.9rem', fontFamily: 'inherit',
          background: t.inputBg, color: t.text,
        },
      })}
      {help && (
        <div style={{ marginTop: 4, fontSize: '0.72rem', color: t.mutedText, fontWeight: 400 }}>
          {help}
        </div>
      )}
    </label>
  );
}

const centered = {
  minHeight: '100vh', display: 'flex',
  alignItems: 'center', justifyContent: 'center',
  background: 'hsl(var(--muted) / 0.35)',
};

// ------------------------------------------------------------------
// Analytics tag injection
// ------------------------------------------------------------------
//
// Adds Google Analytics 4, Facebook Pixel, Plausible, and any custom
// head snippet the admin has configured. Returns an array of the DOM
// nodes we added so we can remove them on unmount / template change,
// preventing double-init on client-side navigation.
//
// Each tag is data-testid-tagged (`analytics-*`) so browser tests can
// assert their presence without hitting the third-party network.
function injectAnalytics(wp) {
  if (typeof document === 'undefined' || !wp) return [];
  const injected = [];

  // Small helper — every node we create is tagged so unmount can
  // remove them without touching foreign scripts.
  const add = (node, testId) => {
    node.setAttribute('data-welcome-analytics', testId);
    if (testId) node.setAttribute('data-testid', `analytics-${testId}`);
    document.head.appendChild(node);
    injected.push(node);
    return node;
  };

  // ---------- Google Analytics 4 / Ads / Tag Manager ----------
  const ga = (wp.ga4_measurement_id || '').trim();
  if (/^(G|AW|GT|DC|MC)-[A-Z0-9-]{4,30}$/.test(ga)) {
    const s1 = document.createElement('script');
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga)}`;
    add(s1, 'ga4-loader');
    const s2 = document.createElement('script');
    s2.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', ${JSON.stringify(ga)});`;
    add(s2, 'ga4-config');
  }

  // ---------- Facebook Pixel ----------
  const fb = (wp.facebook_pixel_id || '').trim();
  if (/^\d{6,20}$/.test(fb)) {
    const s = document.createElement('script');
    s.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init', ${JSON.stringify(fb)});fbq('track', 'PageView');`;
    add(s, 'fb-pixel');
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1; img.width = 1; img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${encodeURIComponent(fb)}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    add(noscript, 'fb-pixel-noscript');
  }

  // ---------- Plausible (privacy-friendly, cookie-less) ----------
  const pl = (wp.plausible_domain || '').trim().toLowerCase();
  if (/^[a-z0-9.-]+\.[a-z]{2,}$/.test(pl)) {
    const s = document.createElement('script');
    s.async = true;
    s.defer = true;
    s.setAttribute('data-domain', pl);
    // Use the "manual + outbound" combo so `plausible('Signup')` works.
    s.src = 'https://plausible.io/js/script.manual.outbound-links.js';
    add(s, 'plausible-loader');
    const s2 = document.createElement('script');
    s2.text = `window.plausible=window.plausible||function(){(window.plausible.q=window.plausible.q||[]).push(arguments)};plausible('pageview');`;
    add(s2, 'plausible-init');
  }

  // ---------- Custom head snippet (advanced escape hatch) ----------
  const custom = (wp.custom_head_snippet || '').trim();
  if (custom) {
    // We can't use innerHTML on the <head> directly because <script>
    // tags inserted via innerHTML don't execute. Parse the fragment,
    // rebuild each element, then append — this is the standard trick
    // used by 3rd-party tag managers like Shopify's & Squarespace's
    // "custom head" fields.
    try {
      const tpl = document.createElement('template');
      tpl.innerHTML = custom;
      Array.from(tpl.content.childNodes).forEach((node, i) => {
        if (node.nodeType === 1 /* ELEMENT */ && node.tagName === 'SCRIPT') {
          const s = document.createElement('script');
          Array.from(node.attributes).forEach((a) => s.setAttribute(a.name, a.value));
          s.text = node.textContent || '';
          add(s, `custom-${i}`);
        } else if (node.nodeType === 1) {
          add(node.cloneNode(true), `custom-${i}`);
        }
      });
    } catch (_e) { /* silently swallow — a broken snippet must not crash the page */ }
  }

  return injected;
}

// Expose templates registry for the admin editor's picker to consume.
export { TEMPLATES };
